<template>
    <div class="virtual-gallery-container">
        <!-- HomePage View -->
        <div v-if="homePage" class="home-container">
            <component :is="dynamicComponent" :homeImage="homeImage" :exhibitIdProps="exhibitId" @exit="exitHomePage" />
        </div>

        <!-- 3D Gallery View -->
        <div v-else class="gallery-wrapper">

            <!-- 3D Viewport -->
            <div class="viewport">
                <div class="world" ref="worldRef">

                    <!-- Intro Text at Z=0 -->
                    <div class="gallery-intro item-3d" :style="{ transform: 'translateZ(0px)' }">
                        <h1 class="main-title">{{ exhibitName }}</h1>
                        <p class="sub-title" v-if="userName">Une exposition par {{ userName }}</p>
                        <div class="scroll-hint">
                            <p>Scrollez pour avancer</p>
                            <div class="arrow-down"></div>
                        </div>
                    </div>

                    <!-- Resources distributed in Z -->
                    <div v-for="(resource, index) in resources" :key="index" class="art-frame item-3d"
                        :style="getItemStyle(index)" @click="openLightbox(index)">
                        <div class="art-content">
                            <img :src="resourceUrls[index]" :alt="resource.title" loading="lazy" />
                            <div class="art-label">
                                <h3>{{ truncateText(resource.title, 30) }}</h3>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <!-- Scroll Spacer (creates the scrollable height) -->
            <div class="scroll-spacer" :style="{ height: spacerHeight + 'px' }"></div>

            <!-- UI Overlays (Fixed) -->
            <div class="ui-overlay">
                <button class="back-btn" @click="homePage = true">Menu</button>
                <div class="progress-bar" :style="{ width: progress + '%' }"></div>
            </div>

            <!-- Lightbox -->
            <transition name="fade">
                <div v-if="selectedImageIndex !== null" class="lightbox" @click="closeLightbox">
                    <div class="lightbox-content" @click.stop>
                        <button class="lightbox-close" @click="closeLightbox">&times;</button>
                        <img :src="resourceUrls[selectedImageIndex]" class="lightbox-img" />
                        <div class="lightbox-info">
                            <h2>{{ resources[selectedImageIndex].title }}</h2>
                            <p>{{ resources[selectedImageIndex].description }}</p>
                        </div>
                    </div>
                </div>
            </transition>

        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineProps } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import gsap from 'gsap';

// Import Models
import Modele_1 from "../../components/Models/Modele_1.vue";
import Modele_2 from "../../components/Models/Modele_2.vue";
import Modele_3 from "../../components/Models/Modele_3.vue";
import Modele_4 from "../../components/Models/Modele_4.vue";
import Modele_5 from "../../components/Models/Modele_5.vue";
import Modele_6 from "../../components/Models/Modele_6.vue";
import Modele_7 from "../../components/Models/Modele_7.vue";

const props = defineProps({
    homePageProps: { type: Boolean, default: true },
    exhibitId: { type: String, default: null }
});

const route = useRoute();
const id = ref(props.exhibitId ?? route.params.id);
const homePage = ref(props.homePageProps);
const selectedTemplate = ref("");
const exhibitName = ref("");
const userName = ref("");
const homeImage = ref({});
const resources = ref([]);
const resourceUrls = ref([]);

// 3D Logic
const worldRef = ref(null);
const Z_SPACING = 800; // Distance between items
const spacerHeight = ref(5000);
const progress = ref(0);
const selectedImageIndex = ref(null);

// Models
const modeleComponents = { Modele_1, Modele_2, Modele_3, Modele_4, Modele_5, Modele_6, Modele_7 };
const dynamicComponent = computed(() => modeleComponents[selectedTemplate.value] || null);

// Data Fetching
const fetchData = async () => {
    try {
        const baseUrl = process.env.VUE_APP_BASEURL;
        const exhibitParam = id.value;

        const [exhibitRes, userRes, homeRes, modelRes] = await Promise.all([
            axios.get(`${baseUrl}/exhibit/${exhibitParam}`),
            axios.get(`${baseUrl}/user/${exhibitParam}`),
            axios.get(`${baseUrl}/exhibit_home/${exhibitParam}`),
            axios.get(`${baseUrl}/model/${exhibitParam}`)
        ]);

        exhibitName.value = exhibitRes.data.exhibit_name;
        userName.value = userRes.data.name || userRes.data.organisation;
        homeImage.value = homeRes.data[0];

        if (modelRes.data.length > 0) {
            selectedTemplate.value = modelRes.data[0].model.replace(/è/g, 'e').replace(/\s+/g, '_');
        }

        // Resources
        const resourcesRes = await axios.post(`${baseUrl}/resources`, { exhibitId: exhibitParam });
        const galleryRes = await axios.get(`${baseUrl}/galleryResources/${exhibitParam}`);
        const galleryIds = galleryRes.data.map(r => r.resource_id);
        const filtered = resourcesRes.data.filter(r => galleryIds.includes(r.id));
        resources.value = filtered;

        const urls = await Promise.all(filtered.map(r => axios.get(`${baseUrl}/s3Resource/${r.name}`)));
        resourceUrls.value = urls.map(r => r.data);

        // Update spacer height
        spacerHeight.value = (filtered.length + 1) * Z_SPACING + window.innerHeight;

    } catch {
        resources.value = [];
        resourceUrls.value = [];
    }
};

// 3D Positioning
const getItemStyle = (index) => {
    // Start after the intro (index + 1)
    const zPos = -(index + 1) * Z_SPACING;

    // Alternating X positions (zigzag)
    const xPos = index % 2 === 0 ? '-25vw' : '25vw';
    // Slight Y variation
    const yPos = (index % 3 - 1) * 100 + 'px';

    return {
        transform: `translate3d(${xPos}, ${yPos}, ${zPos}px)`
    };
};

// Scroll Handling
const handleScroll = () => {
    if (homePage.value) return;

    const scrollTop = window.scrollY;
    const maxScroll = spacerHeight.value - window.innerHeight;
    progress.value = Math.min((scrollTop / maxScroll) * 100, 100);

    // Animate world Z based on scroll
    // We move the world positive Z to simulate camera moving negative Z (forward)
    gsap.to(worldRef.value, {
        z: scrollTop,
        duration: 1,
        ease: "power2.out"
    });
};

const exitHomePage = () => {
    homePage.value = false;
    // Reset scroll on exit
    setTimeout(() => window.scrollTo(0, 0), 100);
};

// Helpers
const truncateText = (text, len) => !text || text === 'undefined' ? '' : text.length > len ? text.substring(0, len) + '...' : text;
const openLightbox = (i) => selectedImageIndex.value = i;
const closeLightbox = () => selectedImageIndex.value = null;

// Lifecycle
onMounted(() => {
    fetchData();
    window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700&family=Lato:wght@300;400&display=swap');

.virtual-gallery-container {
    background: #000;
    min-height: 100vh;
    color: #fff;
    font-family: 'Lato', sans-serif;
}

/* 3D Styles */
.gallery-wrapper {
    position: relative;
    /* Fixed viewport effectively */
}

.viewport {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    perspective: 1000px;
    /* Depth of field */
    overflow: hidden;
    background: radial-gradient(circle at center, #1b1b1b 0%, #000 100%);
}

.world {
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    position: absolute;
    top: 0;
    left: 0;
    /* World starts at 0,0,0 */
}

.item-3d {
    position: absolute;
    top: 50%;
    left: 50%;
    /* Start centered, translate from there */
    transform-style: preserve-3d;
    /* use margins to perfectly center content before transform */
    margin-left: -150px;
    /* Half of art-frame width */
    margin-top: -200px;
    /* Half of art-frame height */
}

/* Intro Section */
.gallery-intro {
    margin-left: -300px;
    margin-top: -150px;
    width: 600px;
    height: 300px;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.main-title {
    font-family: 'Cinzel', serif;
    font-size: 4rem;
    margin: 0;
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
}

.sub-title {
    font-size: 1.2rem;
    opacity: 0.8;
    letter-spacing: 3px;
}

.scroll-hint {
    margin-top: 3rem;
    opacity: 0.6;
    animation: pulse 2s infinite;
}

/* Art Frames */
.art-frame {
    width: 300px;
    height: 400px;
    cursor: pointer;
    transition: opacity 0.5s;
}

.art-frame:hover {
    filter: brightness(1.2);
}

.art-content {
    width: 100%;
    height: 100%;
    position: relative;
    background: #fff;
    padding: 10px;
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
}

.art-content img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.art-label {
    position: absolute;
    bottom: -40px;
    width: 100%;
    text-align: center;
    color: #ccc;
    font-size: 0.9rem;
    opacity: 0;
    transition: opacity 0.3s;
}

.art-frame:hover .art-label {
    opacity: 1;
}

/* Spacer */
.scroll-spacer {
    width: 1px;
}

/* UI Overlay */
.ui-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 100;
    padding: 20px;
    display: flex;
    justify-content: space-between;
    pointer-events: none;
    /* Let clicks pass through */
}

.back-btn {
    pointer-events: auto;
    background: transparent;
    border: 1px solid #fff;
    color: #fff;
    padding: 10px 20px;
    cursor: pointer;
    transition: all 0.3s;
}

.back-btn:hover {
    background: #fff;
    color: #000;
}

.progress-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    height: 4px;
    background: #d4af37;
    /* Gold */
}

/* Lightbox */
.lightbox {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.95);
    z-index: 200;
    display: flex;
    justify-content: center;
    align-items: center;
}

.lightbox-content {
    max-width: 90%;
    max-height: 90%;
    text-align: center;
    position: relative;
}

.lightbox-img {
    max-width: 100%;
    max-height: 80vh;
    box-shadow: 0 0 50px rgba(0, 0, 0, 0.8);
}

.lightbox-info {
    margin-top: 1rem;
}

.lightbox-close {
    position: absolute;
    top: -40px;
    right: -40px;
    background: none;
    border: none;
    color: #fff;
    font-size: 2rem;
    cursor: pointer;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@keyframes pulse {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(10px);
    }
}
</style>
